import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Download, ImageOff, Camera, RefreshCw, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PreviewImage = ({ src, alt }) => {
  const [status, setStatus] = useState('loading');
  const [attempt, setAttempt] = useState(0);
  return (
    <div className="relative w-full h-full">
      {status === 'loading' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-stone-400">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="text-xs">Preparing your image...</p>
        </div>
      )}
      {status === 'error' ? (
        <button
          type="button"
          onClick={() => { setStatus('loading'); setAttempt(a => a + 1); }}
          className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-stone-500 hover:text-stone-700"
          data-testid="preview-retry-btn"
        >
          <RefreshCw className="h-8 w-8" />
          <p className="text-xs">Preview didn't load — tap to retry</p>
        </button>
      ) : (
        <img
          key={attempt}
          src={attempt > 0 ? `${src}&retry=${attempt}` : src}
          alt={alt}
          className={`w-full h-full object-contain transition-opacity duration-300 ${status === 'loaded' ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setStatus('loaded')}
          onError={() => setStatus('error')}
        />
      )}
    </div>
  );
};

export default function DigitalDownloadsPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/downloads/${token}`)
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.detail || 'Could not load your downloads'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
        <div className="text-center max-w-md">
          <ImageOff className="h-14 w-14 text-stone-300 mx-auto mb-4" />
          <h1 className="text-2xl font-heading text-stone-800 mb-2">Downloads Unavailable</h1>
          <p className="text-stone-500" data-testid="downloads-error">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 py-10 px-4" data-testid="digital-downloads-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <Camera className="h-10 w-10 text-amber-600 mx-auto mb-3" />
          <h1 className="text-3xl sm:text-4xl font-heading text-stone-800">Your Digital Downloads</h1>
          <p className="text-stone-600 mt-2">
            {data.child_name ? `Full-resolution images for ${data.child_name}` : 'Your full-resolution images'} — yours to keep, print, and share
          </p>
          <p className="text-sm text-stone-400 mt-1">You can return to this page anytime to re-download.</p>
        </div>

        {data.files.length === 0 ? (
          <div className="text-center py-16">
            <ImageOff className="h-12 w-12 text-stone-300 mx-auto mb-4" />
            <p className="text-stone-500">No digital images are ready yet. Please contact us if you believe this is an error.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {data.files.map((file) => (
              <Card key={file.index} className="border-stone-200 overflow-hidden" data-testid={`download-card-${file.index}`}>
                <div className="bg-stone-100 aspect-[4/5] overflow-hidden">
                  <img
                    src={`${BACKEND}${file.preview_url}`}
                    alt={file.label}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-stone-800 truncate">{file.label}</p>
                    <p className="text-xs text-stone-400 truncate">{file.filename}</p>
                  </div>
                  <Button asChild className="bg-amber-700 hover:bg-amber-800 shrink-0" data-testid={`download-btn-${file.index}`}>
                    <a href={`${BACKEND}${file.download_url}`} download={file.filename}>
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <p className="text-center text-xs text-stone-400 mt-10">
          Bright Eyes Photography — questions? Just reply to your download email.
        </p>
      </div>
    </div>
  );
}
